import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdAcUnit, MdSubscriptions } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { getMemberInitials, getGymMemberId } from './memberData';
import MemberForm from './MemberForm';
import {
  fetchMembersPage,
  fetchMembersExport,
  fetchActiveSubscriptionMemberIdsFor,
  createMember,
  deleteMember,
} from '../../../services/receptionist/receptionistApi';
import { fetchBranchNames } from '../../../services/gym/catalogApi';
import { pkTodayKey } from '../../../utils/pkTime';
import TablePagination from '../../../components/shared/TablePagination';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import TableRowIconActions from '../../../components/shared/TableRowIconActions';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  formatDateFilterValue,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import {
  exportMemberCsv,
  exportMemberExcel,
  exportMemberPdf,
  exportMembers,
  printMemberList,
} from './exportMembers';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './members.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function MemberAvatar({ member }) {
  return (
    <div className="member-avatar-sm">
      {member.photoPreview ? (
        <img src={member.photoPreview} alt={member.name} />
      ) : (
        getMemberInitials(member.name)
      )}
    </div>
  );
}

function MemberManagement() {
  const toast = usePanelToast();
  const navigate = useNavigate();
  const [memberList, setMemberList] = useState([]);
  const [activeSubscriptionIds, setActiveSubscriptionIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [branchOptions, setBranchOptions] = useState([]);
  const [adding, setAdding] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const pageSize = 50;
  const requestIdRef = useRef(0);

  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    serialNo: '',
    member: '',
    phone: '',
    email: '',
    branch: '',
    trainer: '',
    joiningDate: '',
    status: '',
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedFilters, rangeStart, rangeEnd]);

  const loadData = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      setLoading(true);
      const result = await fetchMembersPage({
        page,
        pageSize,
        search: debouncedSearch,
        filters: { ...debouncedFilters, rangeStart, rangeEnd, dateFrom: rangeStart, dateTo: rangeEnd },
      });
      if (reqId !== requestIdRef.current) return;
      const rows = result.rows || [];
      setMemberList(rows);
      setTotal(result.total || 0);
      if (result.fromRpc) {
        setActiveSubscriptionIds(new Set(rows.filter((m) => m.hasActiveSubscription).map((m) => m.id)));
      } else {
        try {
          const ids = await fetchActiveSubscriptionMemberIdsFor(rows.map((m) => m.id));
          if (reqId === requestIdRef.current) setActiveSubscriptionIds(ids);
        } catch {
          if (reqId === requestIdRef.current) setActiveSubscriptionIds(new Set());
        }
      }
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setError(err.message || 'Failed to load members');
      setMemberList([]);
      setTotal(0);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, debouncedFilters, rangeStart, rangeEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const branches = await fetchBranchNames();
        if (active) setBranchOptions(branches || []);
      } catch {
        if (active) setBranchOptions([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const openAddModal = () => {
    setFormKey((k) => k + 1);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    if (adding) return;
    setShowAddModal(false);
  };

  const handleAddSubmit = async (form) => {
    try {
      setAdding(true);
      setError('');
      await createMember({
        ...form,
        joiningDate: form.joiningDate || pkTodayKey(),
      });
      setShowAddModal(false);
      setPage(1);
      toast.showAdded('Member');
      await loadData();
    } catch (err) {
      const message = err.message || 'Failed to add member';
      setError(message);
      toast.showError(message);
    } finally {
      setAdding(false);
    }
  };

  const pageItems = memberList;

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const handleExport = async (runExport) => {
    try {
      const result = await fetchMembersExport({
        search: debouncedSearch,
        filters: { ...debouncedFilters, rangeStart, rangeEnd, dateFrom: rangeStart, dateTo: rangeEnd },
      });
      runExport(result.rows || []);
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      setError('');
      await deleteMember(deleteTarget.id);
      setDeleteTarget(null);
      toast.showDeleted('Member');
      await loadData();
    } catch (err) {
      const message = err.message || 'Failed to delete member';
      setError(message);
      toast.showError(message);
    } finally {
      setDeleting(false);
    }
  };

  const openSubscriptionPage = (member) => {
    if (activeSubscriptionIds.has(member.id)) {
      toast.showError('This member already has an active subscription.');
      return;
    }
    navigate('/admin/subscriptions', {
      state: {
        customer: member.name,
        customerId: member.id,
        customerGymId: getGymMemberId(member) || '',
      },
    });
  };

  const openFreezePage = (member) => {
    navigate('/admin/freeze', {
      state: {
        freezeMember: {
          id: member.id,
          name: member.name,
          gymId: getGymMemberId(member) || '',
          phone: member.mobile || member.phone || '',
          email: member.email || '',
          branch: member.branch || '',
          package: member.package || '',
          status: member.status || '',
        },
      },
    });
  };

  return (
    <div>
      <PageHeader
        title="Members"
        subtitle="Manage gym members and registrations"
        variant="banner"
      >
        <button type="button" className="owner-btn owner-btn-primary" onClick={openAddModal}>
          Add Member
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => handleExport(printMemberList)}
        onCsv={() => handleExport(exportMemberCsv)}
        onPdf={() => handleExport(exportMemberPdf)}
        onExcel={() => handleExport(exportMemberExcel)}
        onExport={() => handleExport(exportMembers)}
        ariaLabel="Member print and export"
      >
        <input
          type="search"
          className="owner-form-input"
          style={{ maxWidth: 260 }}
          placeholder="Search name, serial, or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search members"
        />
      </PrintActionsHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading members...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="serialNo"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label="Email"
                  filterKey="email"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search email..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="trainer"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search trainer..."
                />
                <ColumnFilterHeader
                  label="Joining Date"
                  filterKey="joiningDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length > 0 ? (
                pageItems.map((member) => (
                  <tr key={member.id}>
                    <td className="rec-member-gym-id">{getGymMemberId(member) || '—'}</td>
                    <td>
                      <div className="member-name-cell">
                        <MemberAvatar member={member} />
                        {member.name}
                      </div>
                    </td>
                    <td>{member.phone}</td>
                    <td>{member.email}</td>
                    <td>{member.branch || '—'}</td>
                    <td>{member.trainer}</td>
                    <td>{member.joiningDate}</td>
                    <td><StatusBadge status={member.status} /></td>
                    <td>
                      <TableRowIconActions
                        onEdit={() => navigate(`/admin/members/edit/${member.id}`)}
                        onDelete={() => setDeleteTarget(member)}
                        editLabel={`Edit ${member.name}`}
                        deleteLabel={`Delete ${member.name}`}
                      >
                        {!activeSubscriptionIds.has(member.id) && (
                          <button
                            type="button"
                            className="rec-member-icon-btn rec-member-icon-btn-subscription"
                            onClick={() => openSubscriptionPage(member)}
                            aria-label={`Subscription for ${member.name}`}
                            title="Subscription"
                          >
                            <MdSubscriptions />
                          </button>
                        )}
                        <button
                          type="button"
                          className="rec-member-icon-btn rec-member-icon-btn-freeze"
                          onClick={() => openFreezePage(member)}
                          aria-label={`Freeze ${member.name}`}
                          title="Freeze"
                        >
                          <MdAcUnit />
                        </button>
                      </TableRowIconActions>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading…' : 'No members found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Member</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="member-modal-message">
              Delete member <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn member-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Member'}
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="subscription-modal-overlay" onClick={closeAddModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-member-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-member-title" className="subscription-modal-title">Add Member</h2>
              <button
                type="button"
                className="subscription-modal-close"
                onClick={closeAddModal}
                aria-label="Close"
                disabled={adding}
              >
                ×
              </button>
            </div>
            <MemberForm
              key={formKey}
              submitLabel={adding ? 'Adding…' : 'Add Member'}
              branchOptions={branchOptions}
              initialData={{ joiningDate: pkTodayKey() }}
              onSubmit={handleAddSubmit}
              onCancel={closeAddModal}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MemberManagement;
